import React, { useEffect, useState } from "react";
import { Dialog } from '@headlessui/react';
import { useTheme } from '../../CommonComponents/ThemeProvider';
import { PackagePlus, Send, X } from "lucide-react";

export default function AddStockModal({ isOpen, onClose, inventory, onSave, selectedBranchId = "" }) {
    const [addQuantity, setAddQuantity] = useState(0);
    const [error, setError] = useState("");
    const { currentTheme } = useTheme();
    const stockLabel = selectedBranchId ? "Stocks Released" : "Stocks Available";
    const actionLabel = selectedBranchId ? "Release Stock" : "Add Stock";
    const quantityLabel = selectedBranchId ? "Release Quantity" : "Add Quantity";
    const quantityPlaceholder = selectedBranchId ? "Enter quantity to release" : "Enter quantity to add";
    const description = selectedBranchId
        ? "Release stock to the selected branch without leaving the page."
        : "Increase the current inventory quantity without leaving the page.";
    const ActionIcon = selectedBranchId ? Send : PackagePlus;
    const warehouseAvailable = Number(inventory?.quantity) || 0;

    useEffect(() => {
        if (isOpen) {
            setAddQuantity(0);
            setError("");
        }
    }, [isOpen]);

    if (!isOpen || !inventory) return null;

    const currentStock = selectedBranchId
        ? (Number(inventory.released_quantity) || 0)
        : (Number(inventory.quantity) || 0);
    const totalStock = currentStock + (Number(addQuantity) || 0);

    const handleSave = () => {
        const parsed = Number(addQuantity);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            setError("Please enter a valid quantity greater than 0.");
            return;
        }
        if (selectedBranchId && parsed > warehouseAvailable) {
            setError(`Only ${warehouseAvailable} item${warehouseAvailable === 1 ? "" : "s"} available in warehouse.`);
            return;
        }
        onSave(parsed);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm"
        >
            <div className="w-full max-w-md">
                <Dialog.Panel className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white p-6 shadow-[0_30px_80px_rgba(15,23,42,0.18)] md:p-7">
                    <div className="absolute inset-x-0 top-0 h-28 bg-[linear-gradient(135deg,rgba(15,23,42,0.05),rgba(148,163,184,0.04),transparent)]" />
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-500 shadow-sm transition hover:text-slate-900"
                        aria-label={`Close ${actionLabel.toLowerCase()} modal`}
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <div className="relative space-y-5">
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: currentTheme.color }}>
                            <ActionIcon className="h-5 w-5" />
                        </div>
                        <div>
                            <Dialog.Title className="text-xl font-semibold tracking-tight text-slate-900">
                                {actionLabel}
                            </Dialog.Title>
                            <p className="mt-1 text-sm text-slate-500">
                                {description}
                            </p>
                            {selectedBranchId && (
                                <p className="mt-2 text-xs font-medium text-amber-700">
                                    Warehouse available: {warehouseAvailable}
                                </p>
                            )}
                        </div>

                        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-left">
                            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Current {stockLabel}</p>
                                <p className="mt-2 text-2xl font-semibold text-slate-900">{currentStock}</p>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2">{quantityLabel}</label>
                                <input
                                    type="number"
                                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-lg font-semibold text-slate-900 shadow-sm transition focus:outline-none"
                                    value={addQuantity}
                                    min={0}
                                    step={1}
                                    inputMode="numeric"
                                    max={selectedBranchId ? warehouseAvailable : undefined}
                                    onChange={e => {
                                        setAddQuantity(e.target.value === "" ? "" : Number(e.target.value));
                                        setError("");
                                    }}
                                    placeholder={quantityPlaceholder}
                                />
                            </div>
                            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">New {stockLabel}</p>
                                <p className="mt-2 text-2xl font-semibold text-emerald-800">
                                    {Number.isFinite(totalStock) ? totalStock : currentStock}
                                </p>
                            </div>
                        </div>

                        {error && (
                            <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                                {error}
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-1">
                            <button
                                onClick={onClose}
                                className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 focus:outline-none"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="rounded-2xl px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)] transition hover:opacity-90 focus:outline-none"
                                style={{ background: `linear-gradient(135deg, ${currentTheme.color}, color-mix(in srgb, ${currentTheme.color} 78%, black))` }}
                            >
                                {actionLabel}
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
